import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import {
  DEFAULT_EXCLUSION_CODES,
  project,
  projectExclusionCodes,
  projectMember,
} from "~/lib/db/schema";

const createProjectSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be less than 255 characters"),
  description: z
    .string()
    .max(2000, "Description must be less than 2000 characters")
    .optional(),
});

const projectIdSchema = z.object({
  projectId: z.string(),
});

export const $createProject = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => createProjectSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, db } = await import("~/lib/server/helpers.server");
    const user = await requireUser();

    const projectId = nanoid();

    const [newProject] = await db
      .insert(project)
      .values({
        id: projectId,
        title: data.title,
        description: data.description,
        userId: user.id,
      })
      .returning();

    // Seed default exclusion codes for the new project
    await db.insert(projectExclusionCodes).values(
      DEFAULT_EXCLUSION_CODES.map((code, index) => ({
        id: nanoid(),
        projectId,
        code: code.code,
        label: code.label,
        description: code.description,
        sortOrder: index,
        isActive: true,
      })),
    );

    return newProject;
  });

export const $getProjects = createServerFn({ method: "GET" }).handler(async () => {
  const { requireUser, db } = await import("~/lib/server/helpers.server");
  const user = await requireUser();

  // Get owned projects and member projects in parallel
  const [ownedProjects, memberships] = await Promise.all([
    db.query.project.findMany({
      where: eq(project.userId, user.id),
    }),
    db.query.projectMember.findMany({
      where: eq(projectMember.userId, user.id),
      with: { project: true },
    }),
  ]);

  // Combine, dedupe, and sort
  const projectMap = new Map(ownedProjects.map((p) => [p.id, p]));
  for (const m of memberships) {
    if (!projectMap.has(m.project.id)) {
      projectMap.set(m.project.id, m.project);
    }
  }

  return Array.from(projectMap.values()).sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  );
});

export const $getProject = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, db } = await import("~/lib/server/helpers.server");
    const user = await requireUser();

    // Single query to get project
    const foundProject = await db.query.project.findFirst({
      where: eq(project.id, data.projectId),
    });

    if (!foundProject) {
      throw new Error("Project not found");
    }

    // Check ownership first (common case, no extra query)
    const isOwner = foundProject.userId === user.id;

    if (!isOwner) {
      // Only check membership if not owner
      const membership = await db.query.projectMember.findFirst({
        where: and(
          eq(projectMember.projectId, data.projectId),
          eq(projectMember.userId, user.id),
        ),
      });

      if (!membership) {
        throw new Error("Unauthorized");
      }
    }

    return { ...foundProject, isOwner };
  });

export const $deleteProject = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, db } = await import("~/lib/server/helpers.server");
    const user = await requireUser();

    const foundProject = await db.query.project.findFirst({
      where: eq(project.id, data.projectId),
    });

    if (!foundProject) {
      throw new Error("Project not found");
    }

    if (foundProject.userId !== user.id) {
      throw new Error("Unauthorized");
    }

    await db.delete(project).where(eq(project.id, data.projectId));

    return { success: true };
  });
